import React from 'react';
import {
  OpsFilterModel,
  OpsSlaAttemptsModel,
  OpsVerticalFunnelModel
} from '../../../../shared/models/lead-ops.model';
import {
  getOpsSlaAttempts,
  getOpsVerticalFunnel
} from '../../../../shared/services/data/lead-ops.data';
import { CHIP, VERTICAL_STYLE, styleFor } from '../../../shared/lead-visuals';
import { useOpsReport } from '../components/use-ops-report.hook';
import { OpsBars, OpsSection, OpsStat, TD, TH } from '../components/ops-section.component';

const titleCase = (value: string): string =>
  (value || '').replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

export const VerticalFunnelSection: React.FC<{ filter: OpsFilterModel }> = ({ filter }) => {
  const { data, loading, error, reload } = useOpsReport<OpsVerticalFunnelModel>(
    getOpsVerticalFunnel,
    filter
  );
  const rows = data?.verticals ?? [];

  return (
    <OpsSection
      title="Vertical funnel"
      subtitle="Each funnel has its own won outcomes — a furnishing lead is won by a quote or a request, an acquisition by a signed contract, a reservation by a booking. Conversion is against every lead taken, because open leads are still in the funnel."
      accent="bg-blue"
      loading={loading}
      error={error}
      empty={!loading && !error && rows.every((row) => row.total === 0)}
      onRetry={reload}
    >
      <div className="grid gap-4 lg:grid-cols-3">
        {rows.map((row) => (
          <div key={row.vertical} className="rounded-xl border border-creams-500 p-4">
            <div className="mb-3 flex items-center justify-between">
              <span className={`${CHIP} ${styleFor(VERTICAL_STYLE, row.vertical)}`}>
                {titleCase(row.vertical)}
              </span>
              <span className="font-gilroy-bold text-xl text-neutral-500">
                {row.conversionRate}%
              </span>
            </div>
            <div className="mb-3 grid grid-cols-3 gap-2 text-center">
              <div>
                <div className="text-xs text-neutral-300">Leads</div>
                <div className="font-gilroy-bold text-neutral-500">{row.total}</div>
              </div>
              <div>
                <div className="text-xs text-neutral-300">Won</div>
                <div className="font-gilroy-bold text-primary-600">{row.won}</div>
              </div>
              <div>
                <div className="text-xs text-neutral-300">Lost</div>
                <div className="font-gilroy-bold text-secondary-600">{row.lost}</div>
              </div>
            </div>
            {row.wonBreakdown.length ? (
              <OpsBars
                rows={row.wonBreakdown}
                label={titleCase}
                tone={() => 'bg-primary-400'}
                emptyText="No wins yet"
              />
            ) : (
              <p className="text-xs text-neutral-300">No wins in this window.</p>
            )}
          </div>
        ))}
      </div>
    </OpsSection>
  );
};

export const SlaAttemptsSection: React.FC<{ filter: OpsFilterModel }> = ({ filter }) => {
  const { data, loading, error, reload } = useOpsReport<OpsSlaAttemptsModel>(
    getOpsSlaAttempts,
    filter
  );

  return (
    <OpsSection
      title="SLA & contact attempts"
      subtitle="Leads with no SLA target recorded are counted separately and never as misses. An unanswered lead past its target counts as breached whether or not it is answered later."
      accent="bg-action-alert"
      loading={loading}
      error={error}
      empty={!!data && data.sla.met + data.sla.breached + data.sla.awaiting === 0}
      onRetry={reload}
    >
      {data ? (
        <div className="flex flex-col gap-5">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <OpsStat
              label="SLA met"
              value={`${data.sla.metRate}%`}
              tone={data.sla.metRate >= 80 ? 'good' : 'bad'}
              hint={`${data.sla.met} of ${data.sla.met + data.sla.breached} with a target`}
            />
            <OpsStat label="Breached" value={data.sla.breached} tone="bad" />
            <OpsStat
              label="Median first response"
              value={
                data.medianFirstResponseMinutes === null
                  ? '—'
                  : data.medianFirstResponseMinutes < 60
                  ? `${data.medianFirstResponseMinutes}m`
                  : `${Math.round((data.medianFirstResponseMinutes / 60) * 10) / 10}h`
              }
              hint={data.medianFirstResponseMinutes === null ? 'Nothing answered yet' : undefined}
            />
            <OpsStat
              label="No target"
              value={data.sla.unknown}
              hint="Not a miss — no SLA was recorded"
            />
          </div>

          <div className="grid gap-5 lg:grid-cols-2">
            <div>
              <h3 className="mb-2 font-gilroy-medium text-sm text-neutral-400">
                Time to first response
              </h3>
              <OpsBars rows={data.firstResponseBuckets} tone={() => 'bg-primary-400'} />
            </div>
            <div>
              <h3 className="mb-2 font-gilroy-medium text-sm text-neutral-400">Contact attempts</h3>
              <div className="grid grid-cols-2 gap-3">
                <OpsStat label="Never called" value={data.attempts.untouched} tone="bad" />
                <OpsStat label="Quota spent" value={data.attempts.exhausted} />
                <OpsStat
                  label="Closed unreachable"
                  value={data.attempts.unreachable}
                  hint="Quota ran out and the service closed it"
                />
                <OpsStat label="Average attempts" value={data.attempts.averageAttempts} />
              </div>
            </div>
          </div>

          {data.slaByVertical.length ? (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[420px] text-sm">
                <thead>
                  <tr className="bg-creams-200">
                    {['Vertical', 'Met', 'Breached', 'Met rate'].map((c: string) => (
                      <th key={c} className={TH}>
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.slaByVertical.map((row) => (
                    <tr key={row.vertical} className="hover:bg-creams-100">
                      <td className={TD}>
                        <span className={`${CHIP} ${styleFor(VERTICAL_STYLE, row.vertical)}`}>
                          {titleCase(row.vertical)}
                        </span>
                      </td>
                      <td className={`${TD} text-neutral-400`}>{row.met}</td>
                      <td className={`${TD} text-neutral-400`}>{row.breached}</td>
                      <td className={TD}>
                        <span
                          className={row.metRate >= 80 ? 'text-primary-600' : 'text-secondary-600'}
                        >
                          {row.met + row.breached === 0 ? '—' : `${row.metRate}%`}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      ) : null}
    </OpsSection>
  );
};
